import React, { useState, useRef } from 'react';
import {
  DndContext,
  PointerSensor,
  useSensor,
  useSensors,
  closestCenter,
} from '@dnd-kit/core';
import {
  useSortable,
  SortableContext,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { restrictToVerticalAxis } from '@dnd-kit/modifiers';

import DeleteConfirmation from 'components/DeleteConfirmation';
import ChecklistItem from './ChecklistItem';


const SortableChecklistItem = (props) => {
  const { taskId } = props;

  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({
    id: taskId,
  });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.6 : 1,
  };

  return (
    <div
      ref={setNodeRef}
      style={style}
    >
      <ChecklistItem
        {...props}
        dragHandleAttributes={attributes}
        dragHandleListeners={listeners}
      />
    </div>
  );
};

/** @typedef {import('./types.js').ChecklistVisibility} ChecklistVisibility */
/** @typedef {import('./types.js').DateString} DateString */
/** @typedef {import('./types.js').Timeframe} Timeframe */

/**
 * @param {object} props
 * @param {Array<object>} props.list - List of checklist items
 * @param {number} props.currentLoginId - login id of user
 * @param {string} props.timeZone - timezone of user
 * @param {string} props.dateFormat - Format string for displaying dates (e.g., 'MMM D, YYYY').
 * @param {string} props.timeFormat - Format string for displaying times (e.g., 'h:mm A').
 * @param {Array<object>} props.assignablePeople - A list of people that can be assigned to a lead task.
 * @param {object} props.assignments - An object keyed by task id that contains a list of assignments.
 * @param {object} props.reminders - An object keyed by task id that contains a list of reminders.
 * @param {number} props.preselectedTask - id of task that should be opened when list renders.
 * @param {function(number, string): void} props.onChangeTitle - function to change title
 * @param {function(number,
 *  { type: 'date'|'timeframe', value: DateString|Timeframe }): void} props.onChangeDueDate - function to change duedate
 * @param {function(ChecklistVisibility): void} props.onChangeVisibility - function to change visibility
 * @param {function(number, string): void} props.onChangeDetails - function to change detail field (taskId, value)
 * @param {function(number, boolean): void} props.onCheck - function to mark the task as complete (taskId, value)
 * @param {function(number, string): void} props.onChangeAssignmentNote - function change pre assignment note
 * @param {function(number, number): void} props.onAssign - function to add an assignment (loginId, taskId)
 * @param {function(number, number): void} props.onUnassign - function to remove an assignment (loginId, taskId)
 * @param {function(number): void} props.onDelete - function to delete the task (taskId)
 * @param {function(number): void} props.onAddItem - function to add a task above this task
 * @param {object} props.comments - Comments to display in the checklist items.
 * @param {function(number): void} props.getComments - function to fetch comments
 * @param {function(number, number): void} props.addComment - function to add a comment
 * @param {function(number, number, number): void} props.editComment - function to edit a comment
 * @param {function(number, number): void} props.deleteComment - function to delete a comment
 * @param {function(): void} props.onChangeHighlighted - function to update highlighted
 * @param {function(number): void} props.getReminders - function to fetch reminders for a task
 * @param {function(object): void} props.createTaskReminder - function to create a task reminder
 * @param {function(object): void} props.updateTaskReminder - function to update a task reminder
 * @param {function(object): void} props.deleteTaskReminder - function to delete a task reminder
 * @param {boolean} [props.isSortable=false] - whether the checklist items are sortable
 * @param {function(): void} props.onReorder - function to reorder tasks
 * @param {boolean} props.collapseOnOutsideClick - flag if item should collapse on outside click.
 */
const Checklist = ({
  list = [],
  currentLoginId,
  timeZone,
  dateFormat,
  timeFormat,
  assignablePeople,
  assignments,
  reminders,
  preselectedTask,
  onChangeTitle,
  onChangeDueDate,
  onChangeVisibility,
  onChangeDetails,
  onCheck,
  onChangeAssignmentNote,
  onAssign,
  onUnassign,
  onDelete,
  onAddItem,
  comments,
  getComments,
  addComment,
  editComment,
  deleteComment,
  onChangeHighlighted,
  getReminders,
  createTaskReminder,
  updateTaskReminder,
  deleteTaskReminder,
  isSortable = false,
  onReorder,
  collapseOnOutsideClick,
}) => {
  const taskToDelete = useRef(null);
  const [deleteConfirmOpened, setDeleteConfirmOpened] = useState(false);

  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: { distance: 10 },
    }),
  );

  const handleDragEnd = ({ active, over }) => {
    onReorder?.({ active, over });
  };

  const handleDelete = (taskId) => {
    taskToDelete.current = taskId;
    setDeleteConfirmOpened(true);
  };

  const deleteTask = () => {
    if (taskToDelete.current) onDelete(taskToDelete.current);
    setDeleteConfirmOpened(false);
  };

  const renderChecklistItem = (item) => {
    const ChecklistItemComponent = isSortable ? SortableChecklistItem : ChecklistItem;

    return (
      <ChecklistItemComponent
        key={item.id}
        taskId={item.id}
        title={item.title}
        dueDate={item.dueDate}
        timeframe={item.timeframe}
        visibility={item.visibility}
        details={item.details}
        completed={item.completed}
        assignmentNote={item.assignmentNote}
        assignablePeople={assignablePeople}
        assigned={assignments?.[item.id] || []}
        comments={comments?.[item.id] || []}
        reminders={reminders?.[item.id] || []}
        highlighted={item.highlighted}
        currentLoginId={currentLoginId}
        timeZone={timeZone}
        dateFormat={dateFormat}
        timeFormat={timeFormat}
        preopened={preselectedTask && parseInt(preselectedTask, 10) === item.id}
        onChangeTitle={(title) => onChangeTitle(item.id, title)}
        onChangeDueDate={(payload) => onChangeDueDate(item.id, payload)}
        onChangeVisibility={onChangeVisibility}
        onChangeDetails={(details) => onChangeDetails(item.id, details)}
        onCheck={(completed) => onCheck(item.id, completed)}
        onDelete={() => handleDelete(item.id)}
        onAddItem={() => onAddItem(item.id)}
        onChangeAssignmentNote={(note) => onChangeAssignmentNote(item.id, note)}
        onAssign={(loginId) => onAssign(loginId, item.id)}
        onUnassign={(loginId) => onUnassign(loginId, item.id)}
        getComments={() => getComments(item.id)}
        addComment={(commentId, comment) => addComment(item.id, commentId, comment)}
        editComment={(commentId, comment) => editComment(item.id, commentId, comment)}
        deleteComment={(commentId) => deleteComment(item.id, commentId)}
        onChangeHighlighted={onChangeHighlighted}
        getReminders={() => getReminders(item.id)}
        createTaskReminder={createTaskReminder}
        updateTaskReminder={updateTaskReminder}
        deleteTaskReminder={deleteTaskReminder}
        isSortable={isSortable}
        collapseOnOutsideClick={collapseOnOutsideClick}
      />
    );
  };

  return (
    <>
      <div className="checklist">
        {isSortable ? (
          <DndContext
            sensors={sensors}
            collisionDetection={closestCenter}
            modifiers={[restrictToVerticalAxis]}
            onDragEnd={handleDragEnd}
          >
            <SortableContext
              items={list}
              strategy={verticalListSortingStrategy}
            >
              {list.map(item => renderChecklistItem(item))}
            </SortableContext>
          </DndContext>
        ) : (
          list.map(item => renderChecklistItem(item))
        )}
      </div>

      <DeleteConfirmation
        show={deleteConfirmOpened}
        handleClose={() => {
          setDeleteConfirmOpened(false);
        }}
        handleConfirm={deleteTask}
        deleteText="Delete"
        size="xs"
      >
        <p>
          Deleting this task will <strong>permanently</strong> remove it from this checklist.
        </p>
      </DeleteConfirmation>
    </>
  );
};

export default Checklist;
